import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Search } from 'lucide-react';

export interface PaletteItem {
  key: string;
  group: 'Steps' | 'Actions';
  title: string;
  sub?: string | undefined;
  icon: ReactNode;
  run: () => void;
}

/**
 * ⌘K "jump to a step or run an action". A modal dialog whose input is a
 * combobox driving a listbox via aria-activedescendant; focus stays in the
 * input (Tab is trapped) and returns to where it came from on close.
 */
export function CommandPalette({ items, onClose }: { items: PaletteItem[]; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const returnTo = useRef<Element | null>(null);
  const uid = useId();

  useLayoutEffect(() => {
    returnTo.current = document.activeElement;
    input.current?.focus();
    const back = returnTo;
    return () => {
      const el = back.current;
      if (el instanceof HTMLElement && el.isConnected) el.focus({ preventScroll: true });
    };
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => `${x.title} ${x.sub ?? ''}`.toLowerCase().includes(q));
  }, [items, query]);
  const active = Math.min(sel, Math.max(0, shown.length - 1));

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, shown]);

  const optId = (i: number) => `${uid}-opt-${i}`;

  function onKeyDown(e: KeyboardEvent) {
    const n = Math.max(1, shown.length);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((active + 1) % n);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((active - 1 + n) % n);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      shown[active]?.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === 'Tab') {
      e.preventDefault();
    }
  }

  let group = '';
  return (
    <>
      <div className="pf-scrim" onClick={onClose} aria-hidden="true" />
      <div
        className="pf-cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Jump to a step"
        onKeyDown={onKeyDown}
      >
        <div className="pf-cmdk-in">
          <Search aria-hidden="true" />
          <input
            ref={input}
            role="combobox"
            aria-label="Search steps and actions"
            aria-expanded="true"
            aria-controls={`${uid}-list`}
            aria-autocomplete="list"
            aria-activedescendant={shown.length ? optId(active) : undefined}
            placeholder="Jump to a step or run an action"
            autoComplete="off"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSel(0);
            }}
          />
          <kbd>esc</kbd>
        </div>
        <div
          className="pf-cmdk-res"
          id={`${uid}-list`}
          role="listbox"
          aria-label="Steps and actions"
          ref={listRef}
        >
          {shown.length === 0 ? (
            <div className="pf-cmdk-empty" role="presentation">
              No step or action matches.
            </div>
          ) : null}
          {shown.map((x, i) => {
            const heading = x.group !== group ? x.group : null;
            group = x.group;
            return (
              <div key={x.key} role="presentation">
                {heading ? (
                  <div className="pf-cmdk-grp" role="presentation">
                    {heading}
                  </div>
                ) : null}
                <div
                  id={optId(i)}
                  role="option"
                  aria-selected={i === active}
                  className="pf-cmdk-it"
                  onMouseMove={() => {
                    if (i !== active) setSel(i);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => x.run()}
                >
                  {x.icon}
                  <span>{x.title}</span>
                  {x.sub ? <small>{x.sub}</small> : <span />}
                </div>
              </div>
            );
          })}
        </div>
        <div className="pf-cmdk-foot" aria-hidden="true">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> move
          </span>
          <span>
            <kbd>↵</kbd> select
          </span>
        </div>
      </div>
    </>
  );
}
